import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, ShieldAlert, FileBox, FileText, UserCheck, LogOut } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Truncated } from '@/components/common/Truncated';
import { liveApi } from '@/api/endpoints';

function shortTs(ts: any): string {
  if (!ts) return '';
  const d = new Date(typeof ts === 'string' ? ts : ts.$date || ts);
  if (isNaN(d.getTime())) return String(ts).substring(0, 19);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function LiveDrillDrawer({ hwid, onClose }: { hwid: string; onClose: () => void; }) {
  const { t } = useTranslation();
  const [data, setData] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    liveApi.drill(hwid).then(setData).catch(e => setErr(e?.message));
  }, [hwid]);

  const mark = data?.mark && Object.keys(data.mark).length > 0 ? data.mark : null;
  const cheat = data?.detections?.cheat || [];
  const dll = data?.detections?.dll || [];
  const files = data?.detections?.files || [];
  const auths = data?.sessions?.auths || [];
  const disconnects = data?.sessions?.disconnects || [];
  const nicks = data?.nicks || [];

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full max-w-2xl bg-background border-l shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-background border-b px-4 py-3 flex items-start justify-between z-10">
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold flex items-center gap-2">{t('live.drill.title')}</h2>
            <div className="font-mono text-xs text-muted-foreground break-all">{hwid}</div>
            <div className="text-xs text-muted-foreground mt-1">{t('live.drill.window')}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </header>

        <div className="p-4 space-y-5">
          {!data && !err && (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('live.drill.loading')}
            </div>
          )}
          {err && <div className="text-sm text-destructive">{t('common.error')}: {err}</div>}
          {data && (
            <>
              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t('live.drill.currentMark')}</h3>
                {mark ? (
                  <div className="border rounded p-3 text-sm">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={mark.status === 'KNOWN_CHEATER' ? 'destructive' : 'secondary'}>{mark.status}</Badge>
                      <span className="text-xs text-muted-foreground">{t('marks.severity').toLowerCase()} {mark.severity ?? '—'}</span>
                    </div>
                    {mark.notes && <div className="text-xs text-muted-foreground">{mark.notes}</div>}
                    {Array.isArray(mark.tags) && mark.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {mark.tags.map((tag: string) => <Badge key={tag} variant="outline" className="text-[10px]">{tag}</Badge>)}
                      </div>
                    )}
                  </div>
                ) : <div className="text-sm text-muted-foreground italic">{t('live.drill.noMark')}</div>}
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t('live.drill.sessions', { auths: auths.length, disc: disconnects.length })}</h3>
                <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
                  {[...auths.map((e: any) => ({ ...e, _kind: 'auth' })), ...disconnects.map((e: any) => ({ ...e, _kind: 'disc' }))]
                    .sort((a, b) => (new Date(b.ts).getTime() - new Date(a.ts).getTime()))
                    .slice(0, 30)
                    .map((e: any, i: number) => (
                      <div key={i} className="flex items-center gap-2 py-0.5 border-b border-dashed">
                        <span className="font-mono text-muted-foreground">{shortTs(e.ts)}</span>
                        {e._kind === 'auth' ? <UserCheck className="h-3 w-3 text-emerald-600" /> : <LogOut className="h-3 w-3 text-muted-foreground" />}
                        <span className="flex-1 truncate">{e.event || e.detail || '—'}</span>
                        {e.ipAddress && <span className="font-mono text-[10px] text-muted-foreground">{e.ipAddress}</span>}
                      </div>
                    ))}
                  {auths.length === 0 && disconnects.length === 0 && <div className="text-muted-foreground italic">{t('live.drill.noEvents')}</div>}
                </div>
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
                  <ShieldAlert className="h-3 w-3" /> {t('live.drill.cheatDetections')} ({cheat.length})
                </h3>
                {cheat.length === 0 ? <div className="text-sm text-muted-foreground italic">{t('live.drill.none')}</div> : (
                  <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                    {cheat.slice(0, 15).map((d: any, i: number) => (
                      <div key={i} className="border rounded p-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-muted-foreground">{shortTs(d.ts)}</span>
                          <Badge variant="destructive" className="text-[10px]">{d.detectionType || d.type || 'CHEAT'}</Badge>
                          {d.riskLevel != null && <span className="text-xs text-destructive">risk {d.riskLevel}</span>}
                        </div>
                        {d.message && <div className="mt-1 text-muted-foreground"><Truncated value={d.message} maxChars={120} /></div>}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
                  <FileBox className="h-3 w-3" /> {t('live.drill.dllDetections')} ({dll.length})
                </h3>
                {dll.length === 0 ? <div className="text-sm text-muted-foreground italic">{t('live.drill.none')}</div> : (
                  <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                    {dll.slice(0, 15).map((d: any, i: number) => (
                      <div key={i} className="border rounded p-2">
                        <div className="font-mono text-[11px]"><Truncated value={d.modulePath || d.fileName || '—'} maxChars={70} /></div>
                        <div className="text-muted-foreground mt-0.5">{shortTs(d.ts)} · risk {d.riskLevel ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
                  <FileText className="h-3 w-3" /> {t('live.drill.fileDetections')} ({files.length})
                </h3>
                {files.length === 0 ? <div className="text-sm text-muted-foreground italic">{t('live.drill.none')}</div> : (
                  <div className="space-y-1 text-xs max-h-40 overflow-y-auto">
                    {files.slice(0, 15).map((d: any, i: number) => (
                      <div key={i} className="border rounded p-2">
                        <div className="font-mono text-[11px]"><Truncated value={d.modulePath || d.fileName || '—'} maxChars={70} /></div>
                        <div className="text-muted-foreground mt-0.5">{shortTs(d.ts)} · {d.signatureStatus || '—'} · risk {d.riskLevel ?? '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-2">{t('live.drill.nicks')} ({nicks.length})</h3>
                {nicks.length === 0 ? <div className="text-sm text-muted-foreground italic">{t('live.drill.noNicks')}</div> : (
                  <ul className="text-xs space-y-0.5 max-h-32 overflow-y-auto">
                    {nicks.slice(0, 20).map((n: any, i: number) => (
                      <li key={i} className="flex gap-2 items-baseline">
                        <span className="font-mono text-muted-foreground">{shortTs(n.ts)}</span>
                        <span className="font-medium">{n.nickname || n.characterName || '—'}</span>
                        {n.ipAddress && <span className="text-muted-foreground">{n.ipAddress}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
